import React, { useState, useEffect, useCallback } from 'react';
import { ResponsiveButton } from '../../../common/ResponsiveLayout';
import * as toolConfigService from '../../../../services/toolConfigService';
import ToolList from './ToolList';
import ToolModal from './ToolModal';
import toast from 'react-hot-toast';

/**
 * ToolsTab Component
 * Manages the list of Vapi tools
 */
const ToolsTab = () => {
  const [tools, setTools] = useState([]);
  const [selectedTool, setSelectedTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // null, true, or false
  const [typeFilter, setTypeFilter] = useState(''); // '', 'function', 'ghl', 'make'

  const fetchTools = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await toolConfigService.getTools({
        search: searchTerm,
        isActive: activeFilter,
        type: typeFilter || null
      });

      if (fetchError) {
        throw new Error(fetchError);
      }
      
      setTools(data || []);
    } catch (err) {
      console.error('Failed to fetch tools:', err);
      setError(err.message || 'Failed to fetch tools');
      toast.error('Failed to fetch tools');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, activeFilter, typeFilter]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const handleCreate = async (toolData) => {
    setOperationLoading(true);
    setError(null);
    
    try {
      const { error } = await toolConfigService.createTool(toolData);

      if (error) {
        throw new Error(error);
      }

      toast.success('Tool created successfully!');
      await fetchTools();
      setIsModalOpen(false);
      setSelectedTool(null);
    } catch (err) {
      console.error('Failed to create tool:', err);
      setError(err.message || 'Failed to create tool');
      toast.error(err.message || 'Failed to create tool');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleUpdate = async (toolId, toolData) => {
    setOperationLoading(true);
    setError(null);
    
    try {
      const { error } = await toolConfigService.updateTool(toolId, toolData);

      if (error) {
        throw new Error(error);
      }

      toast.success('Tool updated successfully!');
      await fetchTools();
      setIsModalOpen(false);
      setSelectedTool(null);
    } catch (err) {
      console.error('Failed to update tool:', err);
      setError(err.message || 'Failed to update tool');
      toast.error(err.message || 'Failed to update tool');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDelete = async (tool) => {
    if (!window.confirm(`Are you sure you want to delete "${tool.name}"? This action cannot be undone.`)) {
      return;
    }

    setOperationLoading(true);
    setError(null);
    
    try {
      const { error } = await toolConfigService.deleteTool(tool.id);

      if (error) {
        throw new Error(error);
      }

      toast.success('Tool deleted successfully!');
      await fetchTools();
    } catch (err) {
      console.error('Failed to delete tool:', err);
      setError(err.message || 'Failed to delete tool');
      toast.error(err.message || 'Failed to delete tool');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleToggleStatus = async (tool) => {
    const newStatus = !tool.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} "${tool.name}"?`)) {
      return;
    }

    setOperationLoading(true);
    setError(null);
    
    try {
      const { error } = await toolConfigService.toggleToolStatus(tool.id, newStatus);

      if (error) {
        throw new Error(error);
      }

      toast.success(`Tool ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      await fetchTools();
    } catch (err) {
      console.error(`Failed to ${action} tool:`, err);
      setError(err.message || `Failed to ${action} tool`);
      toast.error(err.message || `Failed to ${action} tool`);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleEdit = (tool) => {
    setSelectedTool(tool);
    setIsModalOpen(true);
    setError(null);
  };

  const handleCloseModal = () => {
    setSelectedTool(null);
    setIsModalOpen(false);
    setError(null);
  };

  const handleSave = async (formData) => {
    if (selectedTool) {
      await handleUpdate(selectedTool.id, formData);
    } else {
      await handleCreate(formData);
    }
  };

  return (
    <div>
      {/* Header with Actions */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex-1">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              placeholder="Search tools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Types</option>
              <option value="function">Function</option>
              <option value="ghl">GHL</option>
              <option value="make">Make</option>
            </select>
            <select
              value={activeFilter === null ? 'all' : activeFilter.toString()}
              onChange={(e) => {
                const value = e.target.value;
                setActiveFilter(value === 'all' ? null : value === 'true');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
        
        <ResponsiveButton
          onClick={() => {
            setSelectedTool(null);
            setIsModalOpen(true);
            setError(null);
          }}
          variant="primary"
          disabled={operationLoading}
          size={{ xs: 'md', lg: 'md' }}
          className="w-full sm:w-auto"
        >
          Add New Tool
        </ResponsiveButton>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex">
            <div className="flex-1">
              <strong>Error:</strong> {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900 ml-2 font-bold text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Tool List */}
      <ToolList
        tools={tools}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
      />

      {/* Tool Modal */}
      <ToolModal
        isOpen={isModalOpen}
        tool={selectedTool}
        onSave={handleSave}
        onCancel={handleCloseModal}
        loading={operationLoading}
      />
    </div>
  );
};

export default ToolsTab;